
/**
 * Wrap an element in another element
 *
 * @param {element} element - The element you want to wrap
 * @param {string} optional wrapper - The tag you want to wrap around the target element
 * @return {element} The element created
 * @example
 *     wrap(element, `<div class="checklist__inner"></div>`);
 */
export const wrap = (element, wrapper) => {
  // If no wrapper has been passed just use a div
  let wrapperEl = wrapper || document.createElement('div');
  (element.nextSibling) ? element.parentNode.insertBefore(wrapperEl, element.nextSibling) : element.parentNode.appendChild(wrapperEl);
  return wrapperEl.appendChild(element);
};

/**
 * Convert a string to an element
 *
 * @param {str} - A string of the element you want to create
 * @return {element} The element created
 * @example
 *     strToEl(`<div class="checklist"></div>`);
 */
export const strToEl = (function() {
  let tmpEl = document.createElement('div');
  return function(str) {
    let cleanedInput = str.trim();
    let r;
    tmpEl.innerHTML = cleanedInput;
    r = tmpEl.children[0];
    while (tmpEl.firstChild) {
      tmpEl.removeChild(tmpEl.firstChild);
    }

    return r;
  };
}());



/**
 * Find an ancestor by attribute name
 *
 * @param {element} el - The element whose ancestor you want to find
 * @param {string} attr - the attribute the parent must have
 * @return {element} The element if found, or null if not
 * @example
 *     findAncestorByAttrName(element, "data-init");
 */
export const findAncestorByAttrName = (el, attr) => {

  // Set the target as the element.
  // Continuously get the parent element until the element has the attribute
  // we are looking for
  let target = el;

  while (target) {
    if (target.hasAttribute(attr)) return target;
    target = target.parentElement;
  }
  // Return null if we never find anything
  return null;
};



/**
 * Check if object is of a certain type
 *
 * @param {string} type - The type that you want to check against
 * @param {object} obj - The object you want to check
 * @return {boolean} Whether the object matches or not
 * @example
 *     isType("NodeList", element);
 */
export const isType = (type, obj) => {
  let cls = getType(obj);
  return obj !== undefined && obj !== null && cls === type;
};



/**
 * Gets the type of the object
 *
 * @param {object} obj - An object you want the type of
 * @return {string} The type of object as a string
 * @example
 *     getType(obj);
 */
export const getType = (obj) => Object.prototype.toString.call(obj).slice(8, -1);



/**
 * Creates a unique ID with or without a suffix
 *
 * @param {string} suffix - The suffix to add to the end of the unique string
 * @return {string} a string with a random 7 digit string at the beginning, and
 * an optional suffix at the end
 *
 * @example
 *     createUniqueID('dropdown');
 */
export const createUniqueID = (suffix) => `${Math.random().toString(36).substr(2, 9)}_${suffix || ""}`;



/**
 * Returns total height of element including margins
 *
 * @param {element} el - The element you want to find the height of
 * @returns height in pixels
 *
 * @example
 *     outerHeight(anElement);
 */
export const outerHeight = (el) => {
  // We use .currentStyle or getComputedStyle() for browser compatibility
  let style = el.currentStyle || getComputedStyle(el);
  let mt = parseInt(style.marginTop);
  let mb = parseInt(style.marginBottom);

  let t = Number.isNaN(mt) ? 0 : mt;
  let b = Number.isNaN(mb) ? 0 : mb;
  return (el.offsetHeight + t + b);
}


export const dismantleAllInstancesExceptPageLoader = () => {
  Object.keys(HANDLERS).forEach((item, i) => {
    if (!`${item}`.includes("pageLoader")) {
      let handler = HANDLERS[`${item}`];
      handler.dismantleAllAndDeleteInstanceList();
    }
  });
}

export const initAllInstancesExceptPageLoader = (container) => {
  Object.keys(HANDLERS).forEach((item, i) => {
    if (!`${item}`.includes("pageLoader")) {
      let handler = window.HANDLERS[`${item}`];
      handler.init(container);
    }
  });
}

export const isIE = () => {
  let ua = window.navigator.userAgent;
  let msie = ua.indexOf("MSIE ");
  return (msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./));
}

export const isIE11 = () => {
  return !!window.MSInputMethodContext && !!document.documentMode;
}

export const formatNumberWithCommas = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}


/**
 * Creates a cookie
 * origin : https://www.w3schools.com/js/js_cookies.asp
 *
 * @param {string} cName - The name of the cookie to be set
 * @param {string | boolean} cValue - The value of the cookie to be set
 * @param {number} exDays - The number of days until the cookie expires
 * 
 */
export const setCookie = (cName, cValue, exDays) => {
  const d = new Date();
  d.setTime(d.getTime() + (exDays*24*60*60*1000));
  let expires = "expires="+ d.toUTCString();
  document.cookie = cName + "=" + cValue + ";" + expires + ";path=/";
}

/**
 * Reads a cookie
 * origin : https://www.w3schools.com/js/js_cookies.asp
 *
 * @param {string} cName - The name of the cookie to be read
 * 
 * @returns {string | boolean} - value of the cookie if this is set  
 * 
 */
export const getCookie = (cName) => {
  let name = cName + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for(let i = 0; i <ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

/***
 * 
 * Simple javascript throttle helper
 * 
 * @param {function} callback - The function to callback on throttle
 * @param {number} delay - The value in ms of the delay between calls
 * 
 */
export const _throttle = (callback, delay=100) => {
  let timer = null;
  return function(...args) {
    if (timer === null) {
      timer = setTimeout(() => {
        callback.apply(this, args);
        timer = null;
      }, delay); 
    }
  };
}

/***
 * 
 * Simple javascript helper to retrieve the page name
 * 
 * @return {string} - The page name string
 * 
 */
export const currentPageName = () => {
  const path = window.location.pathname;
  const page = path.split("/").pop();
  return page === '' ? 'home' : page;
}

/***
 * 
 * Simple javascript helper return an array of pages where banners should be suppress by default
 * 
 * @return {Array} - Array of suppressed pages
 * 
 */
export const bannerSuppressedPages = () => {
  return [
    "contact",
    "privacy",
    "complaints-policy"
  ];
}

/***
 * 
 * Simple javascript function to check if browser supports webp images
 * from: https://stackoverflow.com/questions/5573096/detecting-webp-support
 * 
 * @return {boolean} - true of false
 * 
 */
export const canUseWebP = () => {
  let elem = document.createElement('canvas');
  if (!!(elem.getContext && elem.getContext('2d'))) {
      return elem.toDataURL('image/webp').indexOf('data:image/webp') == 0;
  }
  return false;
}